import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axios from 'axios';

interface TwoFactorSetupProps {}

interface TwoFAStatus {
  is2FAEnabled: boolean;
  hasBackupCodes: boolean;
}

interface SetupData {
  secret: string;
  qrCode: string;
  backupCodes: string[];
}

const TwoFactorSetup: React.FC<TwoFactorSetupProps> = () => {
  const [status, setStatus] = useState<TwoFAStatus | null>(null);
  const [setupData, setSetupData] = useState<SetupData | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [verificationToken, setVerificationToken] = useState('');
  const [disableToken, setDisableToken] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { t } = useTranslation();

  const steps = [t('auth.status'), t('auth.setup'), t('auth.verification')];

  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/2fa/status`);
      setStatus(response.data);
    } catch (err: any) {
      setError(t('auth.failedToFetchStatus'));
    }
  }, [t]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleSetup = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/2fa/setup`);
      setSetupData(response.data);
      setCurrentStep(1);
    } catch (err: any) {
      setError(err.response?.data?.error || t('auth.failedToSetup2FA'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verificationToken.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/2fa/verify`, {
        token: verificationToken.trim(),
      });
      setSuccess(t('auth.twoFAEnabledSuccess'));
      setCurrentStep(0);
      setVerificationToken('');
      setSetupData(null);
      fetchStatus();
    } catch (err: any) {
      setError(err.response?.data?.error || t('auth.invalid2FAToken'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!disableToken.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/2fa/disable`, {
        token: disableToken.trim(),
      });
      setSuccess(t('auth.twoFADisabledSuccess'));
      setDisableToken('');
      fetchStatus();
    } catch (err: any) {
      setError(err.response?.data?.error || t('auth.invalid2FAToken'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackupVerification = async () => {
    if (!backupCode.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/2fa/backup`, {
        backupCode: backupCode.trim(),
      });
      setSuccess(t('auth.backupCodeVerifiedSuccess'));
      setBackupCode('');
      fetchStatus();
    } catch (err: any) {
      setError(err.response?.data?.error || t('auth.invalidBackupCode'));
    } finally {
      setIsLoading(false);
    }
  };

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  if (!status) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>{t('common.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        {t('auth.twoFactorAuth')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearMessages}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={clearMessages}>
          {success}
        </Alert>
      )}

      <Stepper activeStep={currentStep} sx={{ mb: 3, mt: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {currentStep === 0 && (
        // Status Step
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SecurityIcon sx={{ mr: 1 }} />
              <Typography variant="h6">{t('auth.twoFactorStatus')}</Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" gutterBottom>
                  <strong>2FA Status:</strong> {status.is2FAEnabled ? t('auth.twoFactorEnabled') : t('auth.twoFactorDisabled')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Backup Codes:</strong> {status.hasBackupCodes ? t('auth.backupCodesAvailable') : t('auth.backupCodesNotAvailable')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                {!status.is2FAEnabled ? (
                  <Button
                    variant="contained"
                    startIcon={<SecurityIcon />}
                    onClick={handleSetup}
                    disabled={isLoading}
                    fullWidth
                  >
                    {t('auth.enable2FA')}
                  </Button>
                ) : (
                  <Box>
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={fetchStatus}
                      disabled={isLoading}
                      sx={{ mb: 1 }}
                      fullWidth
                    >
                      {t('auth.refreshStatus')}
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => setCurrentStep(2)}
                      disabled={isLoading}
                      fullWidth
                    >
                      {t('auth.disable2FA')}
                    </Button>
                  </Box>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {currentStep === 1 && setupData && (
        // Setup Step
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('auth.twoFactorSetup')}
            </Typography>

            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('auth.setupInstructions')}
            </Typography>

            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <img
                src={setupData.qrCode}
                alt="QR Code"
                style={{ maxWidth: '200px', height: 'auto' }}
              />
            </Box>

            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('auth.manualEntry')} <code>{setupData.secret}</code>
            </Typography>

            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('auth.verificationInstructions')}
            </Typography>

            <TextField
              fullWidth
              label={t('auth.verificationCode')}
              value={verificationToken}
              onChange={(e) => setVerificationToken(e.target.value)}
              margin="normal"
              inputProps={{
                maxLength: 6,
                pattern: '[0-9]*',
              }}
              placeholder="123456"
            />

            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleVerify}
                disabled={isLoading || !verificationToken.trim()}
                sx={{ mr: 1 }}
              >
                {isLoading ? t('auth.verifying') : t('auth.verifyAndEnable')}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setCurrentStep(0);
                  setSetupData(null);
                  setVerificationToken('');
                }}
                disabled={isLoading}
              >
                {t('common.cancel')}
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              {t('auth.backupCodes')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('auth.backupCodesDescription')}
            </Typography>
            <Box sx={{ 
              p: 2, 
              bgcolor: 'grey.100', 
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.9rem'
            }}>
              {setupData.backupCodes.map((code, index) => (
                <div key={index}>{code}</div>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {currentStep === 2 && (
        // Disable Step
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('auth.disable2FA')}
            </Typography>

            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('auth.disableInstructions')}
            </Typography>

            <TextField
              fullWidth
              label={t('auth.twoFAToken')}
              value={disableToken}
              onChange={(e) => setDisableToken(e.target.value)}
              margin="normal"
              inputProps={{
                maxLength: 6,
                pattern: '[0-9]*',
              }}
              placeholder="123456"
            />

            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="error"
                onClick={handleDisable}
                disabled={isLoading || !disableToken.trim()}
                sx={{ mr: 1 }}
              >
                {isLoading ? t('auth.disabling') : t('auth.disable2FA')}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setCurrentStep(0);
                  setDisableToken('');
                }}
                disabled={isLoading}
              >
                {t('common.cancel')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {status.is2FAEnabled && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('auth.verifyBackupCode')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('auth.verifyBackupCodeDescription')}
            </Typography>

            <TextField
              fullWidth
              label={t('auth.backupCode')}
              value={backupCode}
              onChange={(e) => setBackupCode(e.target.value)}
              margin="normal"
              placeholder="ABC123"
            />

            <Button
              variant="outlined"
              onClick={handleBackupVerification}
              disabled={isLoading || !backupCode.trim()}
              sx={{ mt: 1 }}
            >
              {isLoading ? t('auth.verifyingBackup') : t('auth.verifyBackupCode')}
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default TwoFactorSetup; 